import { PrimaryButton } from "@components/Buttons/PrimaryButton";
import { BaseModalComponent } from "@components/common/BaseModalComponent";
import { WidgetComponentMaps } from "@components/common/WidgetComponentMaps";
import { useSubscriptionInfoQuery } from "@components/subscriptions/hooks/useSubscriptionInfoQuery";
import { SUBSCRIPTION_KEYS } from "@components/subscriptions/queries/subscription.queries";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import { Box, Divider, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { NumbersConfiguration } from "./components/NumbersConfiguration";
import { useCompanyNumberQuery } from "./components/NumbersConfiguration/hooks/useCompanyNumberQuery";
import { COMPANY_NUMBERS_KEY } from "./components/NumbersConfiguration/query/company-numbers.queries";
import { linkPaidPorting } from "./components/PaySetupFee/flow-mobile-stripe.actions";
import {
  PhoneNumberPlanPreview,
  PhoneNumberPreviewRow,
  PhoneNumberPreviewSource,
  PhoneNumberPreviewStatus,
} from "./components/PhoneNumberPreviewRow";

const ACTIVE_SUBSCRIPTION_STATUSES = ["completed", "active", "cancelled", "failed"];

export interface CompanyNumber {
  uid: string;
  companyId: number;
  name: string | null;
  phoneNumber: string;
  status: string;
  source: string;
  provider: string;
  providerPhoneNumberSid?: string | null;
  portingUid?: string | null;
  buyingUid?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface FlowMobilePlanConfig {
  id: string;
  externalId: string;
  name: string;
  features: string[];
  phoneNumber?: string;
  planId: string;
  companyId: number;
  subscriptionId?: string;
  expDate?: string;
  status?: string;
}

interface PhoneNumbersWidgetProps {
  swapyName?: string;
  sx?: any;
}

const getBenefitNames = (benefits?: { name?: string }[]) => {
  return (
    benefits
      ?.map((benefit) => benefit.name)
      .filter((name): name is string => Boolean(name))
      .slice(0, 4) ?? []
  );
};

function getPreviewStatus(companyNumber?: CompanyNumber | null): PhoneNumberPreviewStatus {
  if (!companyNumber) return "SETUP_REQUIRED";

  if (companyNumber.status === "ACTIVE") {
    return "ACTIVE";
  }

  if (companyNumber.status === "PENDING" && companyNumber.source === "PORTING") {
    return "PENDING_PORTING";
  }

  if (companyNumber.status === "PENDING" && companyNumber.source === "BUYING") {
    return "PENDING_ACTIVATION";
  }

  return "SETUP_REQUIRED";
}

function getPreviewSource(companyNumber?: CompanyNumber | null): PhoneNumberPreviewSource | undefined {
  if (!companyNumber) return undefined;

  if (companyNumber.source === "PORTING") {
    return "PORTING";
  }

  if (companyNumber.source === "BUYING") {
    return "BUYING";
  }

  if (companyNumber.source === "PORTED") {
    return "PORTED";
  }

  if (companyNumber.source === "PURCHASED") {
    return "PURCHASED";
  }

  return undefined;
}

export const PhoneNumbersWidget = ({ swapyName = "phoneNumbersWidget", sx }: PhoneNumbersWidgetProps) => {
  const { data, isLoading } = useSubscriptionInfoQuery({
    skipPayPalFetch: true,
    section: "flow_mobile",
  });

  const { data: companyNumbers = [], isLoading: isLoadingCompanyNumbers } = useCompanyNumberQuery();
  const queryClient = useQueryClient();
  const portingLinkAttempts = useRef(new Set<string>());

  const companyNumbersByUid = useMemo(() => {
    return new Map<string, CompanyNumber>(
      companyNumbers.map((companyNumber: CompanyNumber) => {
        return [companyNumber.uid, companyNumber];
      }),
    );
  }, [companyNumbers]);

  const activeSubscriptions = useMemo(() => {
    return (
      data?.data?.subscriptions?.filter((item) => {
        const status = item?.subscription?.status?.toLowerCase();

        return status ? ACTIVE_SUBSCRIPTION_STATUSES.includes(status) : false;
      }) ?? []
    );
  }, [data]);

  const plans: FlowMobilePlanConfig[] = useMemo(() => {
    return activeSubscriptions.map((item) => {
      const subscription = item.subscription;
      const planName = subscription.plan?.product?.name ?? "Flow Mobile Plan";

      const features = getBenefitNames(item.benefits);

      const companyNumber = subscription.externalId ? (companyNumbersByUid.get(subscription.externalId) ?? null) : null;

      return {
        id: subscription.id,
        subscriptionId: subscription.id,
        planId: subscription.planId,
        companyId: subscription.companyId,
        name: planName,
        features,
        externalId: subscription.externalId,
        phoneNumber: companyNumber?.phoneNumber,
        expDate: subscription?.expDate,
        status: subscription?.status,
      };
    });
  }, [activeSubscriptions, companyNumbersByUid]);

  const previewPlans: PhoneNumberPlanPreview[] = useMemo(() => {
    return plans.map((plan): PhoneNumberPlanPreview => {
      const companyNumber = plan.externalId ? (companyNumbersByUid.get(plan.externalId) ?? null) : null;

      return {
        id: plan.id,
        planName: plan.name,
        lineName: companyNumber?.name || "My number",
        phoneNumber: companyNumber?.phoneNumber,
        status: getPreviewStatus(companyNumber),
        source: getPreviewSource(companyNumber),
      };
    });
  }, [plans, companyNumbersByUid]);

  const assigned = useMemo(() => {
    return previewPlans.filter((item) => item.status === "ACTIVE").length;
  }, [previewPlans]);

  const pending = useMemo(() => {
    return previewPlans.filter((item) => item.status !== "ACTIVE").length;
  }, [previewPlans]);

  useEffect(() => {
    const missing = plans.filter((plan) => plan.subscriptionId && !plan.externalId && !portingLinkAttempts.current.has(String(plan.subscriptionId)));
    if (!missing.length) return;
    missing.forEach((plan) => portingLinkAttempts.current.add(String(plan.subscriptionId)));

    let cancelled = false;
    const linkExistingPorting = async () => {
      const results = await Promise.all(missing.map((plan) => linkPaidPorting(String(plan.subscriptionId)).catch(() => null)));
      if (cancelled || !results.some((result) => result?.linked)) return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEYS.self("flow_mobile") }),
        queryClient.invalidateQueries({ queryKey: COMPANY_NUMBERS_KEY }),
      ]);
    };

    void linkExistingPorting();
    return () => {
      cancelled = true;
    };
  }, [plans, queryClient]);

  const isWidgetLoading = isLoading || isLoadingCompanyNumbers;

  return (
    <WidgetComponentMaps
      swapyName={swapyName}
      title="Flow Mobile"
      description="Manage phone numbers connected to your Flow Mobile plans."
      icon={<PhoneInTalkOutlinedIcon />}
      sxProp={{
        ...sx,
        height: { xs: "400px", md: "450px", lg: "100%" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto", px: 1, py: 1 }}>
        {isWidgetLoading ? (
          <Box sx={{ px: 1.5, py: 1.35 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: "var(--fx-surface-textsecondary, #64748B)",
              }}
            >
              Loading Flow Mobile numbers...
            </Typography>
          </Box>
        ) : previewPlans.length === 0 ? (
          <Box sx={{ px: 1.5, py: 1.35 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 900,
                color: "var(--fx-surface-text, #0F172A)",
              }}
            >
              No active Flow Mobile plans
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--fx-surface-textsecondary, #64748B)",
                mt: 0.35,
              }}
            >
              Active subscriptions will appear here.
            </Typography>
          </Box>
        ) : (
          previewPlans.map((item, index) => (
            <Box key={item.id}>
              <PhoneNumberPreviewRow item={item} />

              {index < previewPlans.length - 1 ? <Divider sx={{ opacity: 0.6, mx: 1.5 }} /> : null}
            </Box>
          ))
        )}
      </Box>

      <Box
        data-swapy-no-drag
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1.5,
          mt: "auto",
          borderTop: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.05)",
          bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.8)",
          position: "relative",
          zIndex: 10,
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: "var(--fx-surface-textsecondary, #64748B)",
            lineHeight: 1.2,
          }}
        >
          {assigned} assigned · {pending} pending
        </Typography>

        <BaseModalComponent
          modalKey="Organization-PhoneNumbers"
          modalLabel="Configure Flow Mobile Numbers"
          defaultSizes={{ width: 920, height: 640 }}
          fitContentHeight={false}
          elementTrigger={
            <PrimaryButton variant="outlined" sx={{ width: "100px" }} onClick={() => {}} disabled={isWidgetLoading || plans.length === 0}>
              Settings
            </PrimaryButton>
          }
        >
          <NumbersConfiguration companyNumbers={companyNumbers} plans={plans} />
        </BaseModalComponent>
      </Box>
    </WidgetComponentMaps>
  );
};
